import React, { useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { Card } from './index';
import { TitleFormattedMessage } from '../titleFormattedMessage';
import { paths, useNavigate } from '../../routing';
import { ApiKeysSlideout } from '../apiKeysSlideout';
import { GET_API_KEYS_BY_TEAM } from '../../api/queries/apiKeys';
import { ReactComponent as ExternalIcon } from '../../shared/icons/external-open.svg';
import styles from './styles.module.scss';

type ApiAccessCardProps = {
  teamId: string;
};

interface ApiKeyRow {
  status: string;
  lastUsedAt?: string | null;
}

const formatRelativeOrDate = (value?: string | null): string => {
  if (!value) return 'Never';
  const date = new Date(value);
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export const ApiAccessCard = ({ teamId }: ApiAccessCardProps) => {
  const navigate = useNavigate();
  const [isKeysOpen, setKeysOpen] = useState(false);

  const { data } = useQuery(GET_API_KEYS_BY_TEAM, {
    variables: { teamId },
    skip: !teamId,
    fetchPolicy: 'cache-and-network',
  });

  const keys: ApiKeyRow[] = data?.apiKeysByTeam || [];
  const activeKeys = keys.filter((k) => k.status === 'active');
  const activeCount = activeKeys.length;
  const mostRecentUse = activeKeys
    .map((k) => k.lastUsedAt)
    .filter((v): v is string => !!v)
    .sort()
    .slice(-1)[0];

  const goToApiDocs = () => navigate(paths.apiDocs);

  return (
    <>
      <Card title={null}>
        <div className={styles.apiAccessHeader}>
          <div>
            <h4>
              <FormattedMessage id="title.api-access" defaultMessage="API Access" />
            </h4>
            <span className={styles.subscriptionSubheader}>
              <FormattedMessage id="subtitle.api-access" defaultMessage="Professional plan" />
            </span>
          </div>
          <a
            href="#"
            className={styles.apiDocsLink}
            onClick={(e) => {
              e.preventDefault();
              goToApiDocs();
            }}
          >
            <FormattedMessage id="api-access.view-docs" defaultMessage="View Docs" />
            <ExternalIcon className={styles.apiDocsIcon} />
          </a>
        </div>

        <div className={styles.cardRow}>
          <div>
            <p className={styles.descriptionTitle}>
              <FormattedMessage id="api-access.active-keys" defaultMessage="Active Keys" />
            </p>
            <p>{activeCount}</p>
          </div>
        </div>
        <div className={styles.cardRow}>
          <div>
            <p className={styles.descriptionTitle}>
              <FormattedMessage id="api-access.last-used" defaultMessage="Last Request" />
            </p>
            <p>{formatRelativeOrDate(mostRecentUse)}</p>
          </div>
        </div>

        <Container className={styles.upgradeButtonContainer}>
          <Button variant="primary" block onClick={() => setKeysOpen(true)}>
            <TitleFormattedMessage id="api-access.manage-keys" defaultMessage="Manage API Keys" />
          </Button>
        </Container>
      </Card>

      <ApiKeysSlideout
        isOpen={isKeysOpen}
        onClose={() => setKeysOpen(false)}
        teamId={teamId}
      />
    </>
  );
};
